import { LayaGL } from "../../layagl/LayaGL";
import { SerializeUtil } from "../../loaders/SerializeUtil";
import { Color } from "../../maths/Color";
import { Matrix } from "../../maths/Matrix";
import { Matrix4x4 } from "../../maths/Matrix4x4";
import { Vector2 } from "../../maths/Vector2";
import { Vector4 } from "../../maths/Vector4";
import { RenderTargetFormat } from "../../RenderEngine/RenderEnum/RenderTargetFormat";
import { Shader3D } from "../../RenderEngine/RenderShader/Shader3D";
import { Material } from "../../resource/Material";
import { RenderTexture2D } from "../../resource/RenderTexture2D";
import { ClassUtils } from "../../utils/ClassUtils";
import { ColorUtils } from "../../utils/ColorUtils";
import { PostProcess2DEffect } from "../PostProcess2DEffect";
import { Blit2DCMD } from "../Scene2DSpecial/RenderCMD2D/Blit2DCMD";
const DELTA_INDEX = [0, 0.01, 0.02, 0.04, 0.05, 0.06, 0.07, 0.08, 0.1, 0.11, 0.12, 0.14, 0.15, 0.16, 0.17, 0.18, 0.20, 0.21, 0.22, 0.24, 0.25, 0.27, 0.28, 0.30, 0.32, 0.34, 0.36, 0.38, 0.40, 0.42, 0.44, 0.46, 0.48, 0.5, 0.53, 0.56, 0.59, 0.62, 0.65, 0.68, 0.71, 0.74, 0.77, 0.80, 0.83, 0.86, 0.89, 0.92, 0.95, 0.98, 1.0, 1.06, 1.12, 1.18, 1.24, 1.30, 1.36, 1.42, 1.48, 1.54, 1.60, 1.66, 1.72, 1.78, 1.84, 1.90, 1.96, 2.0, 2.12, 2.25, 2.37, 2.50, 2.62, 2.75, 2.87, 3.0, 3.2, 3.4, 3.6, 3.8, 4.0, 4.3, 4.7, 4.9, 5.0, 5.5, 6.0, 6.5, 6.8, 7.0, 7.3, 7.5, 7.8, 8.0, 8.4, 8.7, 9.0, 9.4, 9.6, 9.8, 10.0];
const GRAY_MATRIX = [0.3086, 0.6094, 0.082, 0, 0, 0.3086, 0.6094, 0.082, 0, 0, 0.3086, 0.6094, 0.082, 0, 0, 0, 0, 0, 1, 0];
const IDENTITY_MATRIX = [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1];
const LENGTH = 25;
export class ColorEffect2D extends PostProcess2DEffect {
    constructor(matrix) {
        super();
        this._colorMat = new Matrix4x4();
        this._centerScale = new Vector2();
        this._alpha = new Vector4();
        this._colorArray = new Float32Array(16);
        this._alphaArray = new Float32Array(4);
        this.setByMatrix(matrix || IDENTITY_MATRIX);
    }
    get colorMat() {
        return this._colorMat;
    }
    set colorMat(value) {
        if (value != this._colorMat) {
            value.cloneTo(this._colorMat);
        }
        this._mat && this._mat.setMatrix4x4("u_colorMat", this.colorMat);
        this._owner && this._owner._onChangeRender();
    }
    gray() {
        return this.setByMatrix(GRAY_MATRIX);
    }
    get alpha() {
        return this._alpha;
    }
    set alpha(value) {
        if (value != this._alpha) {
            value.cloneTo(this._alpha);
        }
        this._mat && this._mat.setVector4("u_colorAlpha", this.alpha);
        this._owner && this._owner._onChangeRender();
    }
    effectInit(postprocess) {
        this._owner = postprocess;
        (!this._mat || this._mat.destroyed) && (this._mat = new Material());
        this._mat.setShaderName("ColorEffect2D");
        this._mat.setMatrix4x4("u_colorMat", this._colorMat);
        this._mat.setVector4("u_colorAlpha", this._alpha);
        this._mat.addDefine(Shader3D.getDefineByName("COLORFILTER"));
        this._centerScale.setValue(1, 1);
        this._mat.setVector2("u_centerScale", this._centerScale);
        this._mat.lock = true;
        if (!this._renderElement) {
            this._renderElement = LayaGL.render2DRenderPassFactory.createRenderElement2D();
            this._renderElement.geometry = Blit2DCMD.InvertQuadGeometry;
            this._renderElement.nodeCommonMap = null;
            this._renderElement.renderStateIsBySprite = false;
            this._renderElement.materialShaderData = this._mat.shaderData;
            this._renderElement.subShader = this._mat.shader.getSubShaderAt(0);
        }
    }
    setByMatrix(matrix) {
        if (this._matrix != matrix) {
            if (!this._matrix)
                this._matrix = [];
            for (var i = 0; i < LENGTH; i++) {
                this._matrix[i] = matrix[i];
            }
        }
        var j = 0;
        var z = 0;
        for (var i = 0; i < 20; i++) {
            if (i % 5 != 4) {
                this._colorArray[j++] = matrix[i];
            }
            else {
                this._alphaArray[z++] = matrix[i];
            }
        }
        this.alpha.setValue(this._alphaArray[0], this._alphaArray[1], this._alphaArray[2], this._alphaArray[3]);
        this.alpha = this.alpha;
        Matrix4x4.TEMP.cloneByArray(this._colorArray);
        this.colorMat = Matrix4x4.TEMP;
        return this;
    }
    color(red = 0, green = 0, blue = 0, alpha = 1) {
        return this.setByMatrix([red, 0, 0, 0, 1, 0, green, 0, 0, 1, 0, 0, blue, 0, 1, 0, 0, 0, alpha, 0]);
    }
    setColor(color) {
        var arr = ColorUtils.create(color).arrColor;
        var mt = [0, 0, 0, 0, 256 * arr[0], 0, 0, 0, 0, 256 * arr[1], 0, 0, 0, 0, 256 * arr[2], 0, 0, 0, 1, 0];
        return this.setByMatrix(mt);
    }
    adjustColor(brightness, contrast, saturation, hue) {
        this.adjustHue(hue);
        this.adjustContrast(contrast);
        this.adjustBrightness(brightness);
        this.adjustSaturation(saturation);
        return this;
    }
    adjustBrightness(brightness) {
        brightness = this._clampValue(brightness, 100);
        if (brightness == 0 || isNaN(brightness))
            return this;
        return this._multiplyMatrix([1, 0, 0, 0, brightness, 0, 1, 0, 0, brightness, 0, 0, 1, 0, brightness, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1]);
    }
    adjustContrast(contrast) {
        contrast = this._clampValue(contrast, 100);
        if (contrast == 0 || isNaN(contrast))
            return this;
        var x;
        if (contrast < 0) {
            x = 127 + contrast / 100 * 127;
        }
        else {
            x = contrast % 1;
            if (x == 0) {
                x = DELTA_INDEX[contrast];
            }
            else {
                x = DELTA_INDEX[(contrast << 0)] * (1 - x) + DELTA_INDEX[(contrast << 0) + 1] * x;
            }
            x = x * 127 + 127;
        }
        var x1 = x / 127;
        var x2 = (127 - x) * 0.5;
        return this._multiplyMatrix([x1, 0, 0, 0, x2, 0, x1, 0, 0, x2, 0, 0, x1, 0, x2, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1]);
    }
    adjustSaturation(saturation) {
        saturation = this._clampValue(saturation, 100);
        if (saturation == 0 || isNaN(saturation))
            return this;
        var x = 1 + ((saturation > 0) ? 3 * saturation / 100 : saturation / 100);
        var dx = 1 - x;
        var r = 0.3086 * dx;
        var g = 0.6094 * dx;
        var b = 0.0820 * dx;
        return this._multiplyMatrix([r + x, g, b, 0, 0, r, g + x, b, 0, 0, r, g, b + x, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1]);
    }
    adjustHue(hue) {
        hue = this._clampValue(hue, 180) / 180 * Math.PI;
        if (hue == 0 || isNaN(hue))
            return this;
        var cos = Math.cos(hue);
        var sin = Math.sin(hue);
        var r = 0.213;
        var g = 0.715;
        var b = 0.072;
        return this._multiplyMatrix([r + cos * (1 - r) + sin * (-r), g + cos * (-g) + sin * (-g), b + cos * (-b) + sin * (1 - b), 0, 0, r + cos * (-r) + sin * (0.143), g + cos * (1 - g) + sin * (0.140), b + cos * (-b) + sin * (-0.283), 0, 0, r + cos * (-r) + sin * (-(1 - r)), g + cos * (-g) + sin * (g), b + cos * (1 - b) + sin * (b), 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1]);
    }
    reset() {
        return this.setByMatrix(IDENTITY_MATRIX);
    }
    _multiplyMatrix(matrix) {
        var col = [];
        this._matrix = this._fixMatrix(this._matrix);
        for (var i = 0; i < 5; i++) {
            for (var j = 0; j < 5; j++) {
                col[j] = this._matrix[j + i * 5];
            }
            for (j = 0; j < 5; j++) {
                var val = 0;
                for (var k = 0; k < 5; k++) {
                    val += matrix[j + k * 5] * col[k];
                }
                this._matrix[j + i * 5] = val;
            }
        }
        return this.setByMatrix(this._matrix);
    }
    _fixMatrix(matrix = null) {
        if (matrix == null)
            return IDENTITY_MATRIX;
        if (matrix.length < LENGTH)
            matrix = matrix.slice(0, matrix.length).concat(IDENTITY_MATRIX.slice(matrix.length, LENGTH));
        else if (matrix.length > LENGTH)
            matrix = matrix.slice(0, LENGTH);
        return matrix;
    }
    _clampValue(val, limit) {
        return Math.min(limit, Math.max(-limit, val));
    }
    render(context) {
        if (!this._destRT || this._destRT.width != context.indirectTarget.width || context.indirectTarget.height != this._destRT.height) {
            if (this._destRT)
                this._destRT.destroy();
            this._destRT = new RenderTexture2D(context.indirectTarget.width, context.indirectTarget.height, RenderTargetFormat.R8G8B8A8);
        }
        this._mat.setTexture("u_MainTex", context.indirectTarget);
        context.command.setRenderTarget(this._destRT, true, Color.CLEAR);
        context.command.drawRenderElement(this._renderElement, Matrix.EMPTY);
        context.destination = this._destRT;
    }
    onAfterDeserialize() {
        if (SerializeUtil.hasProp("_color")) {
            let arr = ColorUtils.create(this._color || "#FFFFFF").arrColor;
            this.color(arr[0], arr[1], arr[2], arr[3]);
        }
        if (SerializeUtil.hasProp("_brightness", "_contrast", "_saturation", "_hue"))
            this.adjustColor(this._brightness || 0, this._contrast || 0, this._saturation || 0, this._hue || 0);
    }
    destroy() {
        this._destRT && (this._destRT.destroy());
        this._mat && (this._mat.destroy());
        this._renderElement && (this._renderElement.destroy());
    }
}
ClassUtils.regClass("ColorEffect2D", ColorEffect2D);
export class GrayscaleEffect2D extends ColorEffect2D {
    constructor() {
        super(GRAY_MATRIX);
    }
}
ClassUtils.regClass("GrayscaleEffect2D", GrayscaleEffect2D);
